import React, { useState } from 'react'

import * as S from './styles'
import { ButtonIcon, Popover, Text } from '..'

export type ActionDropdownOption = {
  text: string
  onClick: () => void
  path?: string
  subOptions?: {
    text: string
    onClick: () => void
    path: string
  }[]
}

export type ActionsDropdownProps = {
  /**
   * Icon of the button
   *
   * @example
   * ```tsx
   * import { ListIcon } from '@/assets/icons'
   *
   * <ActionsDropdown icon={ListIcon} />
   * ```
   *
   */
  icon: string

  /**
   * Alt of the icon
   *
   * @example
   * ```tsx
   * <ActionsDropdown alt="Menu" />
   * ```
   *
   */
  alt: string

  /**
   *  Options of the dropdown
   *
   * @example
   * ```tsx
   * const options = [{text: 'Option 1', onClick: () => console.log('Option 1')}]
   *
   * <ActionsDropdown options={options} />
   * ```
   *
   * @default []
   *
   */
  options: ActionDropdownOption[]

  /**
   * Class name of the button
   *
   * @example
   * ```tsx
   * <ActionsDropdown className="button-icon" />
   * ```
   *
   */
  buttonClassName?: string

  /**
   * Class name of the popover
   *
   * @example
   * ```tsx
   * <ActionsDropdown className="popover" />
   * ```
   *
   */
  popoverClassName?: string

  /**
   * Vertical position of the popover
   *
   * @example
   * ```tsx
   * <Popover verticalAlign="top" />
   * ```
   *
   * @default 'bottom'
   *
   */
  verticalAlign?: 'top' | 'bottom' | 'center'

  /**
   * Horizontal position of the popover
   *
   * @example
   * ```tsx
   * <Popover horizontalAlign="left" />
   * ```
   *
   * @default 'left'
   *
   */
  horizontalAlign?: 'left' | 'right' | 'center'
}

export const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  icon,
  alt,
  options,
  buttonClassName,
  popoverClassName,
  verticalAlign = 'bottom',
  horizontalAlign = 'left',
}) => {
  const [open, setOpen] = useState(false)

  return (
    <S.Container>
      <ButtonIcon
        icon={icon}
        alt={alt}
        onClick={() => setOpen(!open)}
        className={buttonClassName}
      />

      {open && (
        <Popover
          verticalAlign={verticalAlign}
          horizontalAlign={horizontalAlign}
          className={popoverClassName}
        >
          {options.map((option) => (
            <Text
              key={option.text}
              size="b1"
              color="primary"
              onClick={option.onClick}
            >
              {option.text}
            </Text>
          ))}
        </Popover>
      )}
    </S.Container>
  )
}
